import { videoUrlToEmbed, detectVideoPlatform } from '../../utils/videoEmbed';
import { useI18n } from '../../context/useI18n';

function isZoomShareUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const host = new URL(url.trim()).hostname.replace(/^www\./, '').toLowerCase();
    return host === 'zoom.us' || host.endsWith('.zoom.us');
  } catch {
    return /zoom\.us/i.test(url);
  }
}

export default function ClassRecordingPreviewModal({ item, onClose, wide = false }) {
  const { t } = useI18n();
  if (!item) return null;
  const embed = videoUrlToEmbed(item.video_url);
  const title = item.title || t('recording.default_title', 'Grabación');
  const zoomShare = isZoomShareUrl(item.video_url);
  const platform = detectVideoPlatform(item.video_url);
  const panelWidth = wide
    ? 'w-full max-w-full sm:max-w-[min(100%,75vw)]'
    : 'w-full max-w-2xl';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center p-3 sm:p-4 bg-black/60 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`d28d-admin-modal ui-light-panel surface-readable bg-white border border-slate-200 rounded-2xl shadow-2xl ${panelWidth} max-h-[min(92dvh,calc(100dvh-1.5rem))] overflow-y-auto my-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-stone-200 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-stone-900 min-w-0 break-words">{title}</h3>
          <button type="button" className="btn-secondary text-sm shrink-0" onClick={onClose}>
            {t('common.close', 'Cerrar')}
          </button>
        </div>
        <div className="p-4 space-y-3">
          {embed ? (
            <div className={`relative w-full rounded-xl overflow-hidden bg-black ${wide ? 'aspect-video max-h-[min(50dvh,360px)] sm:max-h-[60vh] sm:min-h-0' : 'aspect-video'}`}>
              <iframe
                title={title}
                src={embed}
                className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
              <p className="text-sm text-amber-950 font-medium">
                {zoomShare
                  ? t(
                    'recording.zoom_open_hint',
                    'Las grabaciones de Zoom no se pueden reproducir dentro de la app. Ábrelas en Zoom (pestaña nueva). Si Zoom pide clave o dice que no existe, revisa en tu cuenta Zoom que la grabación siga publicada y la contraseña de compartir.',
                  )
                  : t('recording.embed_fail', 'No se puede incrustar este enlace. Ábrelo en una pestaña nueva.')}
              </p>
              {platform === 'enlace' && !zoomShare ? (
                <p className="text-xs text-stone-600">
                  {t('recording.embed_platforms', 'Para ver el video dentro de la app usa YouTube o Vimeo.')}
                </p>
              ) : null}
            </div>
          )}
          {item.video_url ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <a
                href={item.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center text-sm"
              >
                {zoomShare
                  ? t('recording.open_zoom', 'Abrir grabación en Zoom')
                  : t('recording.open_link', 'Abrir enlace')}
              </a>
              <a
                href={item.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-stone-500 hover:underline break-all"
              >
                {item.video_url}
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
